const getLanguageCandidates = (language: string, includeDefaultLanguage = false) => {
  const normalizedLanguage = language?.split("-")?.[0] || "en";
  return Array.from(
    new Set([language, normalizedLanguage, includeDefaultLanguage ? "en" : ""].filter(Boolean))
  );
};

const getNonEmptyBannerArray = (value: unknown) => {
  return Array.isArray(value) && value.length > 0 ? value : null;
};

export const getImageUri = (value: unknown): string | undefined => {
  if (!value) return undefined;

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (trimmedValue.startsWith("{") || trimmedValue.startsWith("[")) {
      try {
        const parsedValue = JSON.parse(trimmedValue);
        const uri = getImageUri(parsedValue);
        if (uri) return uri;
      } catch {
        return trimmedValue || undefined;
      }
    }

    return trimmedValue || undefined;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const uri = getImageUri(item);
      if (uri) return uri;
    }

    return undefined;
  }

  if (typeof value === "object") {
    const imageObject = value as Record<string, unknown>;

    return (
      getImageUri(imageObject.uri) ||
      getImageUri(imageObject.url) ||
      getImageUri(imageObject.image) ||
      getImageUri(imageObject.images) ||
      getImageUri(Object.values(imageObject))
    );
  }

  return undefined;
};

export const parseBannersConfig = (
  bannerConfig: string | Record<string, any> | undefined,
  language: string,
  isMobile = false
) => {
  if (!bannerConfig) return [];

  try {
    const parsedBannerConfig =
      typeof bannerConfig === "string" ? JSON.parse(bannerConfig) : bannerConfig;
    const languageCandidates = getLanguageCandidates(language);

    if (isMobile) {
      for (const lang of languageCandidates) {
        const byMobileLang = getNonEmptyBannerArray(parsedBannerConfig?.[`mobile_${lang}`]);
        if (byMobileLang) return byMobileLang;
      }

      const mobile = getNonEmptyBannerArray(parsedBannerConfig?.mobile);
      if (mobile) return mobile;

      for (const lang of languageCandidates) {
        const byDesktopLang = getNonEmptyBannerArray(parsedBannerConfig?.[`desktop_${lang}`]);
        if (byDesktopLang) return byDesktopLang;

        const byLang = getNonEmptyBannerArray(parsedBannerConfig?.[lang]);
        if (byLang) return byLang;
      }
    } else {
      for (const lang of languageCandidates) {
        const byDesktopLang = getNonEmptyBannerArray(parsedBannerConfig?.[`desktop_${lang}`]);
        if (byDesktopLang) return byDesktopLang;

        const byLang = getNonEmptyBannerArray(parsedBannerConfig?.[lang]);
        if (byLang) return byLang;
      }

      const desktop = getNonEmptyBannerArray(parsedBannerConfig?.desktop);
      if (desktop) return desktop;
    }
  } catch {
    return [];
  }

  return [];
};

export const getBannerUri = (
  banner: unknown,
  language: string,
  isMobile: boolean
): string | undefined => {
  return (
    getImageUri(parseBannersConfig(banner as string | Record<string, any> | undefined, language, isMobile)) ||
    getImageUri(banner)
  );
};

export const getTranslationBanner = (
  translations: Record<string, Record<string, unknown>> | undefined,
  language: string,
  isMobile: boolean,
  fallback: string,
  preferredFields: string[] = []
): string => {
  if (!translations) return fallback;

  const languageCandidates = getLanguageCandidates(language, true);
  const lobbyFields = [
    ...(isMobile
      ? ["mobileLobbyImg", "mobileLobbyImage", "mobileLobbyBanner"]
      : ["desktopLobbyImg", "desktopLobbyImage", "desktopLobbyBanner"]),
    "lobbyImg",
    "lobbyImage",
    "lobbyBanner",
  ];
  const fallbackFields = isMobile
    ? ["mobileBanner", "banner", ...lobbyFields]
    : ["banner", ...lobbyFields];
  const fields = Array.from(new Set([...preferredFields, ...fallbackFields]));

  for (const lang of languageCandidates) {
    const tr = translations[lang];
    if (!tr) continue;

    for (const field of fields) {
      const uri = getBannerUri(tr[field], lang, isMobile);
      if (uri) return uri;
    }
  }

  return fallback;
};
